/**
 * Simple caching utility with stale-while-revalidate pattern
 * Stores data in localStorage with timestamps
 */

const CACHE_PREFIX = 'app_cache_';
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get cached data
 * @param {string} key - Cache key
 * @returns {Object|null} Cached data or null
 */
export const getCache = (key) => {
    try {
        const item = localStorage.getItem(CACHE_PREFIX + key);
        if (!item) return null;

        const { data, timestamp } = JSON.parse(item);
        return { data, timestamp, isStale: Date.now() - timestamp > DEFAULT_TTL };
    } catch (error) {
        console.error('Cache read error:', error);
        return null;
    }
};

/**
 * Set cache data
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 */
export const setCache = (key, data) => {
    try {
        const item = {
            data,
            timestamp: Date.now(),
        };
        localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(item));
    } catch (error) {
        console.error('Cache write error:', error);
    }
};

/**
 * Clear specific cache
 * @param {string} key - Cache key
 */
export const clearCache = (key) => {
    try {
        localStorage.removeItem(CACHE_PREFIX + key);
    } catch (error) {
        console.error('Cache clear error:', error);
    }
};

/**
 * Clear all app caches
 */
export const clearAllCaches = () => {
    try {
        const keys = Object.keys(localStorage);
        keys.forEach((key) => {
            if (key.startsWith(CACHE_PREFIX)) {
                localStorage.removeItem(key);
            }
        });
    } catch (error) {
        console.error('Cache clear all error:', error);
    }
};

/**
 * Fetch with cache - stale-while-revalidate pattern with loading state support
 * @param {string} cacheKey - Cache key
 * @param {Function} fetchFn - Async function to fetch data
 * @param {Function} onData - Callback when data is available
 * @param {Object} options - Additional options
 * @param {boolean} options.skipCache - Skip reading from cache
 * @param {Function} options.onLoading - Callback when loading starts
 * @param {Function} options.onError - Callback when error occurs
 * @returns {Promise} - Resolves with fresh data
 */
export const fetchWithCache = async (cacheKey, fetchFn, onData, options = {}) => {
    const {
        skipCache = false,
        onLoading,
        onError,
    } = options;
    const persistentCacheAllowed = !cacheKey.startsWith('admin_');

    let cached = null;
    let hasCachedData = false;

    // 1. Try to load from cache immediately (if available and not skipped)
    if (!skipCache && persistentCacheAllowed) {
        try {
            cached = getCache(cacheKey);
            if (cached && cached.data) {
                hasCachedData = true;
                onData(cached.data, true); // true = from cache
                if (onLoading) {
                    onLoading(false);
                }
            }
        } catch (error) {
            console.warn('Cache read failed, proceeding with fetch:', error);
        }
    }

    // 2. Notify about loading state if no cached data
    if (!hasCachedData && onLoading) {
        onLoading(true);
    }

    // 3. Fetch fresh data (always, even if cache exists or fails)
    try {
        const freshData = await fetchFn();

        // 4. Try to update cache (don't fail if this doesn't work)
        try {
            if (persistentCacheAllowed) setCache(cacheKey, freshData);
        } catch (error) {
            console.warn('Cache write failed (continuing anyway):', error);
        }

        // 5. Update UI if data changed or no cache existed
        if (!cached || JSON.stringify(cached.data) !== JSON.stringify(freshData)) {
            onData(freshData, false); // false = fresh data
        }

        // 6. Notify loading complete
        if (!hasCachedData && onLoading) {
            onLoading(false);
        }

        return freshData;
    } catch (error) {
        // 7. Handle error
        if (onError) {
            onError(error);
        }

        // If fetch fails but we have cache, keep using it
        if (cached && cached.data) {
            console.warn('Using stale cache due to fetch error:', error);
            
            // Notify loading complete even with error (showing cached data)
            if (!hasCachedData && onLoading) {
                onLoading(false);
            }
            
            return cached.data;
        }
        
        // Notify loading complete with error
        if (!hasCachedData && onLoading) {
            onLoading(false);
        }
        
        // If no cache and fetch failed, rethrow
        console.error('Fetch failed and no cache available:', error);
        throw error;
    }
};

/**
 * Prefetch and cache data for later use
 * @param {string} cacheKey - Cache key
 * @param {Function} fetchFn - Async function to fetch data
 * @returns {Promise} - Resolves with cached data
 */
export const prefetchWithCache = async (cacheKey, fetchFn) => {
    try {
        // Check if already cached and fresh
        const cached = getCache(cacheKey);
        if (cached && !cached.isStale) {
            return cached.data;
        }

        // Fetch and cache
        const data = await fetchFn();
        setCache(cacheKey, data);
        return data;
    } catch (error) {
        console.warn('Prefetch failed:', error);
        return null;
    }
};

/**
 * Get cache status for a key
 * @param {string} cacheKey - Cache key
 * @returns {Object} - Cache status
 */
export const getCacheStatus = (cacheKey) => {
    const cached = getCache(cacheKey);
    if (!cached) {
        return { exists: false, isStale: false, age: null };
    }

    const age = Date.now() - cached.timestamp;
    return {
        exists: true,
        isStale: cached.isStale,
        age,
        ttl: DEFAULT_TTL,
        remainingTTL: Math.max(0, DEFAULT_TTL - age),
    };
};
